import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../Styles";
import { Overlay } from "../../Components";
import { ButtonTypes } from "../../Definitions";
import NewButton, { NewButtonList } from "./newButton";

/* eslint-disable */
export enum MainContentPaddingTypes {
  primary="primary",
  secondary = "secondary"
}
/* eslint-enable */

interface NewModal_Props {
  modalState?: boolean
  header: string
  subHeader?: string
  headerContent?: JSX.Element
  mainContent?: JSX.Element
  secondaryContent?: JSX.Element
  secondaryContentTop?: JSX.Element
  footerContent?: JSX.Element
  footerMessage?: string
  topRightContent?: JSX.Element
  topLeftContent?: JSX.Element
  disabledTop?: boolean
  predefinedButtons?:{
    onClose?: () => void
    onGoBack?: () => void
    onNext?: () => void
    onPrev?: () => void
  }
  tabButtons?: {
    contents: string[],
    onClick: (i: number) => void,
    startEnhancers: JSX.Element[],
    disabled: number,
    active: number
  }
  overrides?: {
    mainContentPadding?: MainContentPaddingTypes | string
    mainContentScroll?: boolean
    mainContentVisibleOverflow?: boolean
    secondaryContentScroll?: boolean
    headerContentHeight?: string
    backgroundColor?: string
    overlayColor?: string
    footerMessageTopOffset?: string
  }
  responsiveOverrides?: {
    focus_view?: boolean;
    mobile?: boolean
  }
} 

export default function NewModal({modalState=true, header, subHeader, headerContent, secondaryContentTop, mainContent, secondaryContent, footerContent, footerMessage, topRightContent, topLeftContent, disabledTop, overrides, predefinedButtons, tabButtons, responsiveOverrides}:NewModal_Props) {
  const {t} = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(modalState)
  const [isVisible, setIsVisible] = useState<boolean>(modalState);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  useEffect(() => {
    if (modalState) {
      setIsVisible(true);
      setTimeout(() => setIsModalOpen(true), 100);
    } else {
      setIsModalOpen(false);
      setTimeout(() => {
        setIsVisible(false);
      }, 100);
    }
  }, [modalState]);

  // Main Content Padding
  const mainContentPadding = (() => {
    switch (overrides?.mainContentPadding) {
      case MainContentPaddingTypes.secondary:
        return (
          footerContent && mobileXs
            ? "0px 10px 20px 10px"
            : footerContent
              ? "0px 40px 20px 40px"
              : mobileXs
                ? "0px 10px 50px 10px"
                : "0px 40px 50px 40px"
        );
  
      case MainContentPaddingTypes.primary:
        return (
          responsiveOverrides&&responsiveOverrides?.mobile && overrides?.mainContentScroll
            ? "10px 20px"
            : overrides?.mainContentScroll
              ? "20px 10px 20px 20px"
              : responsiveOverrides&&responsiveOverrides?.mobile
                ? "20px 10px"
                : "20px"
        );
  
      default:
        // fallback: hvis man har sendt en streng direkte
        if (typeof overrides?.mainContentPadding === "string") {
          return overrides.mainContentPadding;
        }
        return "0px";
    }
  })();

  // #region | Predefined Buttons
  const CloseBtn = ( predefinedButtons&&predefinedButtons.onClose ?
    <NewButton 
      content={""}
      startEnhancer={glyphs.Close({size: responsiveOverrides&&responsiveOverrides.focus_view ? "25px" : undefined})}
      onClick={() => (predefinedButtons&&predefinedButtons.onClose) ? predefinedButtons.onClose() : undefined} 
      buttonType={ButtonTypes.glyphButton}
      overrides={{
        baseStyles: {}
      }}
    />
  : <></> );

  const GoBackBtn = ( predefinedButtons&&predefinedButtons.onGoBack ?
    <NewButton 
      content={t("buttons.goBack", {defaultValue: "go back"})}
      startEnhancer={glyphs.Arrow({rotate: 180})}
      onClick={predefinedButtons.onGoBack} 
      buttonType={ButtonTypes.textButton}
      overrides={{
        baseStyles: {whiteSpace: "nowrap"}
      }}
    />
    : <></>);
  
  const TabBtns = ( tabButtons ? 
    <NewButtonList
      contents={tabButtons.contents}
      onClick={tabButtons.onClick}
      startEnhancers={tabButtons.startEnhancers}
      buttonType={ButtonTypes.tableButton}
      disabled={(index) => index === tabButtons.disabled ? true : false}
      overrides={(index) => ({
        baseStyles: { backgroundColor: index === tabButtons.active ? colors.blueDarkPrimary : colors.blueDarkSecondary },
        hoverStyles: { backgroundColor: colors.blueDarkPrimary },
        disabledStyles: { opacity: index === tabButtons.disabled ? 1 : 0.5 },
      })}
    />
  : <></>)

  const NextBtn = ( predefinedButtons&&predefinedButtons.onNext ?
    <NewButton 
      focus_view={responsiveOverrides&&responsiveOverrides.focus_view}
      content={t("buttons.next", {defaultValue: "next"})}
      buttonType={ButtonTypes.textButton}
      endEnhancer={glyphs.Arrow({})}
      onClick={() => (predefinedButtons&&predefinedButtons.onNext) ? predefinedButtons.onNext() : undefined} 
    />
  : <></> );

  const PrevBtn = ( predefinedButtons&&predefinedButtons.onPrev ?
    <NewButton 
      focus_view={responsiveOverrides&&responsiveOverrides.focus_view}
      content={t("buttons.prev", {defaultValue: "prev"})}
      buttonType={ButtonTypes.textButton}
      startEnhancer={glyphs.Arrow({rotate: 180})}
      onClick={() => (predefinedButtons&&predefinedButtons.onPrev) ? predefinedButtons.onPrev() : undefined} 
    />
  : <></> );
  // #endregion

    const _headerContentHeight = overrides&&overrides.headerContentHeight ? overrides.headerContentHeight : "50px"

  return ( <>
    {isVisible && createPortal ( <>
      <Overlay 
        overrides={{
          color: overrides&&overrides.overlayColor ? overrides.overlayColor : undefined,
          genericStyle: {opacity: isModalOpen? "1" : "0", transition: "opacity 0.1s ease-out"},
          zIndex: isModalOpen ? 999999999 : 0
        }}
        content={
          <div style={{ height: "fit-content", width: "fit-content", position: "relative", transform: "translate(-50%, -50%)", top: "50%", left: "50%"}}>
            
            {/* Top Nav / Tab btns */}
            <div style={{position: "absolute", top: "-49px", left: "20px", zIndex: isModalOpen ? "1" : "0"}}>
              {TabBtns}
            </div>  

            {/* Modal */}
            <div style={{
              backgroundColor: colors.blueDarkPrimary, 
              width: "fit-content",
              maxWidth: "90vw",
              minWidth: "340px",
              height: "fit-content",
              maxHeight: tabButtons !== undefined ? "80vh" : "90vh", 
              borderRadius: "10px", 
              border: "5px solid "+colors.blueDarkPrimary,
              position: "relative",
              boxSizing: "border-box",
              display: "grid",
              gridTemplateColumns: "100px 1fr",
              gridTemplateRows: `20px 100px ${_headerContentHeight} 1fr fit-content(100%)`,
              zIndex: isModalOpen ? "2" : "0",
              opacity: isModalOpen? "1" : "0",
              transition: "opacity 0.1s ease-out"
            }}>
              
  
            {/* Disabled Top */}
            { disabledTop ? <div style={{ gridArea: "1/1/4/3", position: "relative" }}>
              <Overlay overrides={{ color: colors.transparent }} beforeUnload onClickAlert/>
            </div> : <></>}

            {/* Top right btn area */}
              <div style={{gridArea: "1/1/2/2", display: "flex", justifyContent: "start", alignItems: "start", padding: "5px 0px 0px 10px", zIndex: "2"}}>
                {topRightContent}
                {GoBackBtn}
              </div>

              {/* Top left btn area */}
              <div style={{gridArea: "1/2/2/3", display: "flex", justifyContent: "end", alignItems: "start", padding: "5px 10px 0px 0px", zIndex: "2"}}>
                {topLeftContent}
                {CloseBtn}
              </div>

              {/* Header */}
              <div style={{gridArea: "2/1/3/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0px 20px"}}>
                <p style={{...fonts.header, ...fonts.fontSize0, ...fonts.textShadow, color: colors.greyLightTertiary, textAlign: "center", maxWidth: "690px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{header}</p>
                <p style={{...fonts.header, ...fonts.fontSize2, ...fonts.textShadow, color: colors.greyLightTertiary, textAlign: "center"}}>{subHeader}</p>
              </div>

              {/* Header Content */}
              {(headerContent||(predefinedButtons&&predefinedButtons.onNext)||(predefinedButtons&&predefinedButtons.onPrev)) ?
                <div style={{gridArea: "3/1/4/3", boxSizing: "border-box", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: mobileXs ? "10px" : "20px"}}>
                  {PrevBtn}
                  {headerContent}
                  {!headerContent&&((predefinedButtons&&predefinedButtons.onNext)||(predefinedButtons&&predefinedButtons.onPrev)) ?
                    <div style={{height: "40%", width: "2px", backgroundColor: colors.blueDarkSecondary}}/>
                  : <></>}
                  {NextBtn}
                </div>
              : <></> }

              {/* Content */}
              <div style={{
                gridArea: 
                (headerContent || (predefinedButtons && (predefinedButtons.onNext || predefinedButtons.onPrev))) && footerContent 
                ? "4/1/5/3" 
                : (headerContent || (predefinedButtons && (predefinedButtons.onNext || predefinedButtons.onPrev))) 
                  ? "4/1/6/3"  
                  : footerContent
                    ? "3/1/5/3"  
                    : "3/1/6/3", 
                boxSizing: "border-box", 
                display: "flex", 
                // height: "fit-content",
                alignItems: "center", 
                justifyContent: "center", 
                borderRadius: "0px 0px 8px 8px",
                backgroundColor: overrides&&overrides.backgroundColor? overrides.backgroundColor : colors.blueDarkPrimary,
                overflow: overrides&&overrides.mainContentVisibleOverflow === true ? "visible" : "hidden"
              }}>
                <div style={{
                  height: "100%", 
                  boxSizing: "border-box", 
                  width: "100%",
                  padding: mainContentPadding, 
                  overflow: overrides&&overrides.mainContentScroll ? "auto" : "visible",
                  scrollbarColor: colors.greyTertiary+colors.transparent,
                  scrollbarWidth: "thin",
                  scrollbarGutter: "stable"
                }}>
                  {secondaryContent ? 
                    <div style={{display: "grid", gridTemplateRows: "20px 2px 1fr 2px", gridTemplateColumns: "1fr", gap: "5px", height: "100%"}}>
                      <div style={{ gridArea: "1/1/2/2", display: "flex", justifyContent: "flex-end", padding: "0px 22px", boxSizing: "border-box"}}>
                        { secondaryContentTop ? secondaryContentTop : <></>}
                      </div> 
                      <div style={{ gridArea: "2/1/3/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
                      <div style={{ gridArea: "3/1/4/2", padding: "10px", maxHeight: overrides && overrides.secondaryContentScroll === true ? "40vh" : "fit-content", boxSizing: "border-box", overflowY: overrides && overrides.secondaryContentScroll === true ? "auto" : "hidden", scrollbarColor: colors.blueDarkSecondary + colors.transparent, scrollbarWidth: "thin" }}>
                        { secondaryContent }
                      </div>
                      <div style={{ gridArea: "4/1/5/2",  borderBottom: "2px solid "+colors.blueDarkSecondary }}/>
                    </div>
                  : <></>}
                  {mainContent}
                </div>
              </div>

              {/* Footer */}
              {footerContent ? 
                <div style={{gridArea: "5/1/6/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingBottom: MainContentPaddingTypes.secondary ? "40px" : "20px", position: "relative"}}>
                  {footerContent}
                  <div style={{position: "absolute", top: overrides&&overrides.footerMessageTopOffset ? overrides.footerMessageTopOffset : "55px", ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, transition: "opacity 0.2s ease-in-out", opacity: footerMessage ? "1" : "0"}}>{footerMessage}</div>
                </div> 
              : <></>}

            </div>

          </div>
        }
      />
    </>, document.body)}
  </>)
}